import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function Experiences() {
  const { lang, t, getLocalized } = useLanguage();
  const [experiences, setExperiences] = useState([]);
  const heroZoom = useScrollZoom({ baseScale: 1.12, maxScale: 1.45, zoomSpeed: 0.0006 });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/experiences')
      .then((res) => res.json())
      .then((data) => {
        if (data.items) setExperiences(data.items);
      })
      .catch((err) => console.error(err));
  }, []);

  return (
    <>
      <HeadMeta 
        title={t('experiencesPage.metaTitle') || t('nav.experiences')} 
        description={t('experiencesPage.metaDesc') || 'Udforsk vores udvalgte oplevelser i Hurghada: Snorkling, dykning, luksus bådture, ørkensafari og skræddersyede private ture med personlig vejledning.'}
      />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/snorkling-coral.jpg"
            alt="Captain Hurghada Oplevelser"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal variant="up" duration={800}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 uppercase">
              {t('experiencesPage.heroTitle') || t('experiences.title')}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={200} duration={800}>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {t('experiencesPage.heroSubtitle') || t('experiences.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Grid of Experiences */}
      <section className="py-20 bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {experiences.map((exp, index) => {
              const slug = getLocalized(exp.experience_slug) || exp.experience_slug || exp.slug?.da || exp.slug;
              const title = getLocalized(exp.title);
              const subtitle = getLocalized(exp.subtitle);
              const cardImg = exp.card_image?.[0] || 'https://assets.auxodev.dk/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/hero-redsea.jpg';
              const price = getLocalized(exp.price_from);
              const duration = getLocalized(exp.duration);
              const groupSize = getLocalized(exp.group_size);

              return (
                <ScrollReveal key={exp.id} variant="up" delay={(index % 3) * 100} duration={700}>
                  <div 
                    className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between group h-full"
                  >
                    <div>
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${cardImg.replace('https://assets.auxodev.dk/', '')}`}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />
                        
                        {/* Price Badge */}
                        {price && (
                          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-slate-900 px-3 py-1 rounded-full text-xs font-bold shadow-md border border-slate-100 flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('experiences.fromPrice')}</span>
                            <span className="text-cyan-600 font-bold text-xs">{price}</span>
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="p-6">
                        <h2 className="text-base sm:text-lg font-bold text-slate-900 uppercase tracking-tight mb-2 group-hover:text-cyan-600 transition-colors">
                          {title}
                        </h2>
                        <p className="text-slate-500 text-xs leading-relaxed mb-4 line-clamp-2">
                          {subtitle}
                        </p>

                        <div className="space-y-1.5 text-xs text-slate-600 pt-4 border-t border-slate-100 font-medium">
                          {duration && (
                            <div className="flex items-center gap-2">
                              <i className="fa-solid fa-clock text-cyan-600 text-xs shrink-0" aria-hidden="true" />
                              <span>{duration}</span>
                            </div>
                          )}
                          {groupSize && (
                            <div className="flex items-center gap-2">
                              <i className="fa-solid fa-users text-cyan-600 text-xs shrink-0" aria-hidden="true" />
                              <span>{groupSize}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="p-6 pt-0">
                      <Link
                        to={`/oplevelser/${slug}`}
                        className="w-full py-2.5 px-4 rounded-lg bg-slate-950 hover:bg-slate-800 text-white font-semibold text-xs transition-all flex items-center justify-center gap-2 shadow-sm"
                      >
                        <span>{t('experiences.readMore')}</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

        </div>
      </section>
    </>
  );
}
